// Story 022 + 029 — Split-view overlay.
//
// Sits above the analysis page as a fixed full-bleed surface. Left pane
// pins the primary image (the analyst's current active image); right
// pane shows whatever secondary the analyst picks via the filmstrip.
// In `link-defects` mode the right pane also surfaces a candidate-
// defect list scoped to the secondary image; selecting one + pressing
// Link fires Story 029's `useLinkDefectsMutation`.
//
// Esc closes (AC8). The keyboard handler is wired on the overlay's
// root so it captures regardless of which inner surface holds focus.
import { useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { Viewer, type BoundingBox } from "@aeriosiq/ui";
import type { Defect, ImageRef } from "@aeriosiq/types";
import { useImagesQuery } from "../queries/useImagesQuery.js";
import {
  useDefectsQuery,
  useLinkDefectsMutation,
} from "../../defects/queries.js";
import { formatSeverity } from "../../defects/severityDisplay.js";
import { useImageActivationStore } from "../state/imageActivationStore.js";
import { useEscapeLayer } from "../state/useEscapeLayer.js";
import { ESCAPE_PRIORITY } from "../state/escapeStack.js";
import {
  denyReasonTooltip,
  type DefectEditPermission,
} from "../editPermission.js";
import { useSplitViewStore } from "./splitViewStore.js";
import { SplitViewFilmstrip } from "./SplitViewFilmstrip.js";
import { LinkIcon, XIcon } from "@aeriosiq/ui/icons";

interface SplitViewPanelProps {
  dataAcquisitionId: string;
  editPermission: DefectEditPermission;
}

function imageToOverlayBoxes(
  defects: Defect[],
  image: ImageRef | null,
  highlightId: string | null,
): BoundingBox[] {
  if (!image) return [];
  const W = image.width;
  const H = image.height;
  if (W === 0 || H === 0) return [];
  const out: BoundingBox[] = [];
  for (const d of defects) {
    if (d.imageId !== image.imageId) continue;
    const bb = d.bbox;
    if (!bb) continue;
    out.push({
      id: d.id,
      x: bb.x * W,
      y: bb.y * H,
      width: bb.w * W,
      height: bb.h * H,
      label: d.catalogueLabel,
      ...(d.id === highlightId ? { color: "#00E0FF" } : {}),
    });
  }
  return out;
}

export function SplitViewPanel({
  dataAcquisitionId,
  editPermission,
}: SplitViewPanelProps): React.ReactElement | null {
  const active = useSplitViewStore((s) => s.active);
  const mode = useSplitViewStore((s) => s.mode);
  const primaryImageId = useSplitViewStore((s) => s.primaryImageId);
  const secondaryImageId = useSplitViewStore((s) => s.secondaryImageId);
  const primaryDefectId = useSplitViewStore((s) => s.primaryDefectId);
  const secondaryDefectId = useSplitViewStore((s) => s.secondaryDefectId);
  const close = useSplitViewStore((s) => s.close);
  const setSecondaryDefect = useSplitViewStore((s) => s.setSecondaryDefect);
  const setActiveImage = useImageActivationStore((s) => s.setActive);

  const imagesQuery = useImagesQuery(active ? dataAcquisitionId : "");
  const defectsQuery = useDefectsQuery(active ? dataAcquisitionId : "", {
    pageSize: 200,
  });

  // Esc closes (AC8). Registered with the analysis escape stack
  // (ANA-R3-ESC-01) at the top priority so an open split-view absorbs ESC
  // before any underlying viewer interaction or selection — "split-view
  // above everything" per the 2026-06-09 decision.
  useEscapeLayer({
    id: "analysis:split-view",
    priority: ESCAPE_PRIORITY.splitView,
    isActive: active,
    onEscape: close,
  });

  const images = useMemo<ImageRef[]>(
    () => imagesQuery.data?.items ?? [],
    [imagesQuery.data],
  );
  const defects = useMemo<Defect[]>(
    () => defectsQuery.data?.items ?? [],
    [defectsQuery.data],
  );
  const primaryImage = useMemo<ImageRef | null>(
    () => images.find((i) => i.imageId === primaryImageId) ?? null,
    [images, primaryImageId],
  );
  const secondaryImage = useMemo<ImageRef | null>(
    () => images.find((i) => i.imageId === secondaryImageId) ?? null,
    [images, secondaryImageId],
  );
  const secondaryDefects = useMemo<Defect[]>(
    () =>
      secondaryImage
        ? defects.filter((d) => d.imageId === secondaryImage.imageId)
        : [],
    [defects, secondaryImage],
  );

  const link = useLinkDefectsMutation(dataAcquisitionId);
  const linkAllowed =
    editPermission.allowed &&
    mode === "link-defects" &&
    primaryDefectId &&
    secondaryDefectId &&
    primaryDefectId !== secondaryDefectId;
  const denyTooltip = !editPermission.allowed
    ? denyReasonTooltip(editPermission.reason)
    : !primaryDefectId
      ? "Select a defect to start linking"
      : !secondaryDefectId
        ? "Pick a defect on the right pane to link"
        : "";

  const onLink = async () => {
    if (!linkAllowed) return;
    if (!primaryDefectId || !secondaryDefectId) return;
    await link.mutateAsync({
      masterId: primaryDefectId,
      linkedDefectId: secondaryDefectId,
      dataAcquisitionId,
      imageId: primaryImage?.imageId ?? "",
    });
    close();
  };

  // When the user clicks an option in the filmstrip while split-view
  // is open, our effect (in the host) routes the click to
  // `setSecondaryImage` instead of `setActiveImage`. We DON'T close
  // here — the analyst is browsing for a candidate.
  if (!active) return null;

  return (
    <Box
      role="region"
      aria-label="Split view"
      data-aero-split-view-region
      sx={{
        position: "fixed",
        inset: 0,
        zIndex: (t) => t.zIndex.modal,
        bgcolor: "background.default",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: "divider",
          flexShrink: 0,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          Split view
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {mode === "link-defects"
            ? "Pick a defect on the right pane → press Link"
            : "Comparing two images side-by-side"}
        </Typography>
        <Box sx={{ flexGrow: 1 }} />
        {mode === "link-defects" ? (
          <Tooltip title={denyTooltip}>
            <span>
              <Button
                variant="contained"
                size="small"
                startIcon={<LinkIcon />}
                onClick={() => {
                  void onLink();
                }}
                disabled={!linkAllowed || link.isPending}
                aria-disabled={!linkAllowed}
              >
                Link
              </Button>
            </span>
          </Tooltip>
        ) : null}
        <Tooltip title="Exit split view">
          <IconButton size="small" onClick={close} aria-label="Exit split view">
            <XIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Paper>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          flexGrow: 1,
          minHeight: 0,
        }}
      >
        <SplitPane
          paneKey="primary"
          label="Primary image"
          image={primaryImage}
          overlayBoxes={imageToOverlayBoxes(
            defects,
            primaryImage,
            primaryDefectId,
          )}
          selectedBoxId={primaryDefectId}
          defects={[]} // primary pane is read-only — no candidate picker
          selectedDefectId={null}
          onPickDefect={undefined}
        />
        <SplitPane
          paneKey="secondary"
          label="Secondary image"
          image={secondaryImage}
          overlayBoxes={imageToOverlayBoxes(
            defects,
            secondaryImage,
            secondaryDefectId,
          )}
          selectedBoxId={secondaryDefectId}
          defects={secondaryDefects}
          selectedDefectId={secondaryDefectId}
          onPickDefect={(id) => {
            setSecondaryDefect(id);
          }}
          onCancel={() => {
            // Allow re-activating the right-pane image as the new
            // primary — Aerios-IQ-style "promote secondary" affordance.
            if (secondaryImage) setActiveImage(secondaryImage.imageId);
          }}
        />
      </Box>

      <SplitViewFilmstrip dataAcquisitionId={dataAcquisitionId} />

      {link.isError ? (
        <Snackbar
          open
          autoHideDuration={5000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert severity="error" variant="filled">
            Failed to link defects
            {link.error.errorId ? ` (errorId: ${link.error.errorId})` : ""}
          </Alert>
        </Snackbar>
      ) : null}
    </Box>
  );
}

interface SplitPaneProps {
  paneKey: "primary" | "secondary";
  label: string;
  image: ImageRef | null;
  overlayBoxes: BoundingBox[];
  selectedBoxId: string | null;
  /** Defects to expose as a clickable picker (secondary only). */
  defects: Defect[];
  selectedDefectId: string | null;
  onPickDefect?: ((id: string) => void) | undefined;
  /** Optional bottom-left action — used on secondary pane to promote it. */
  onCancel?: () => void;
}

function SplitPane({
  paneKey,
  label,
  image,
  overlayBoxes,
  selectedBoxId,
  defects,
  selectedDefectId,
  onPickDefect,
  onCancel,
}: SplitPaneProps): React.ReactElement {
  return (
    <Stack
      role="region"
      aria-label={label}
      data-split-pane={paneKey}
      data-image-id={image?.imageId ?? ""}
      sx={{
        position: "relative",
        height: "100%",
        minHeight: 0,
        borderRight: paneKey === "primary" ? 1 : 0,
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: 8,
          left: 8,
          zIndex: 2,
          px: 1,
          py: 0.25,
          bgcolor: "background.paper",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          pointerEvents: "none",
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, textTransform: "uppercase" }}
        >
          {label}
          {image ? ` · ${image.basename}` : ""}
        </Typography>
      </Box>
      <Box sx={{ flexGrow: 1, minHeight: 0, position: "relative" }}>
        {image ? (
          <Viewer
            imageUrl={image.urls.thumb900}
            progressiveImageUrl={image.urls.full}
            fallbackImageUrl={image.urls.thumb900}
            // Distinct from the outer pane's aria-label so the e2e
            // suite's `getByRole('region', { name: /primary image/i })`
            // matches only the pane wrapper.
            ariaLabel={`Split ${paneKey} viewer · ${image.basename}`}
            boundingBoxes={overlayBoxes}
            selectedBoxId={selectedBoxId}
            onBoxClick={(id) => {
              onPickDefect?.(id);
            }}
          />
        ) : (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 4,
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">
              {paneKey === "secondary"
                ? "Pick an image in the filmstrip to compare."
                : "No primary image."}
            </Typography>
          </Box>
        )}
      </Box>
      {onPickDefect && defects.length > 0 ? (
        <>
          <Divider />
          <Box sx={{ maxHeight: 160, overflowY: "auto", flexShrink: 0 }}>
            <List dense disablePadding aria-label={`${label} defects`}>
              {defects.map((d) => (
                <ListItemButton
                  key={d.id}
                  data-defect-id={d.id}
                  selected={selectedDefectId === d.id}
                  onClick={() => {
                    onPickDefect(d.id);
                  }}
                >
                  <ListItemText
                    primary={d.catalogueLabel}
                    // 8.0.0 — nullable severity renders "Not graded".
                    secondary={`${formatSeverity(d.severity)} · ${d.status}`}
                  />
                </ListItemButton>
              ))}
            </List>
          </Box>
          {onCancel ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-start",
                p: 0.5,
                borderTop: 1,
                borderColor: "divider",
              }}
            >
              <Button size="small" onClick={onCancel}>
                Promote to primary
              </Button>
            </Box>
          ) : null}
        </>
      ) : null}
    </Stack>
  );
}
